import React, { useMemo, useEffect } from "react";
import { Table } from "../../../../../shared/Table/Table";
import { useRefunds } from "./hooks/useRefunds";
import classes from "../../../newBusinessInsightsContent.module.scss";
import Header from "../../../components/Header";
import { useHeaderActions } from "../../../hooks/useHeaderActions";
import { Skeleton } from "../../../../../shared/Skeleton/Skeleton";
import { Empty } from "../../../../../shared/Empty/Empty";
import { Box } from "../../../../../shared/Box/Box";
import { LoadMoreObserver } from "../../../../../shared/LoadMoreObserver/LoadMoreObserver";
import { HTTP_ENDPOINTS } from "../../../../../consts/api";

const modalInfo = {
  title: "refundsReport.info.title",
  rows: ["refundsReport.info.note"],
};

const Refunds = () => {
  const { selectedClinic, period, apiPeriod, search } = useHeaderActions();

  // Set default search type for this report
  useEffect(() => {
    // If current search type is from eGift cards report, switch to patient
    if (
      search.type.value === "to" ||
      search.type.value === "from" ||
      search.type.value === "card number"
    ) {
      search.type.update("patient");
      search.update(""); // Clear search term when switching report types
    }
    // If no search type is set or it's not a valid refunds search type, default to patient
    else if (
      !search.type.value ||
      (search.type.value !== "patient" && search.type.value !== "invoice")
    ) {
      search.type.update("patient");
    }
  }, []);

  // Prepare search value
  const _getSearchValue = () => {
    if (!search.value) return "";
    return search.value.trim();
  };

  const requestParams = {
    fromDate: apiPeriod.fromDate,
    toDate: apiPeriod.toDate,
    clinic_id:
      selectedClinic.value.value !== 0 ? [selectedClinic.value.value] : [],
    // Note: search_key temporarily disabled until backend supports it
    // ...(search.value && {
    //   search_key: `${search.type.value}:${getSearchValue()}`,
    // }),
  };
  const { data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage } =
    useRefunds(requestParams);

  const refundLabels = data?.pages[0].refundModeLabels;

  const searchOptions = [
    { label: "Invoice#", value: "invoice" },
    { label: "Patient", value: "patient" },
  ];

  const clinics =
    useMemo(
      () =>
        data?.pages[0]?.clinics?.map((clinic) => ({
          label: clinic.clinic_name,
          value: clinic.id,
        })),
      [data],
    ) || [];

  const tableData = useMemo(() => {
    let filteredData =
      data?.pages
        ?.map((p) => p.reportData)
        .flat()
        .map((report) => ({
          patient: report.patient_name,
          invoiceNumber: report.invoice_number,
          invoiceDate: report.invoice_date,
          refundedAmount: report.refund_amount,
          refundedStatus: report.invoice_status,
          refundedMode: refundLabels[report.refund_mode],
          refundedOn: report.refunded_on,
        })) || [];

    // Apply client-side search filtering
    if (search.value && search.value.trim()) {
      const searchValue = search.value.trim().toLowerCase();

      filteredData = filteredData.filter((item) => {
        if (search.type.value === "patient") {
          return item.patient?.toLowerCase().includes(searchValue);
        } else if (search.type.value === "invoice") {
          return item.invoiceNumber?.toLowerCase().includes(searchValue);
        }
        return true;
      });
    }

    return filteredData;
  }, [data, refundLabels, search.value, search.type.value]);

  const isTableDataEmpty = !isLoading && !tableData?.length;

  return (
    <div className="flex-col font-14 gap-16">
      <Header
        title="Refunds Report"
        selectedClinic={selectedClinic}
        clinics={clinics}
        period={period}
        downloadRoute={HTTP_ENDPOINTS.getRefundsReport()}
        requestParams={requestParams}
        modalInfo={modalInfo}
        search={search}
        showSearch={true}
        searchOptions={searchOptions}
      />
      <div className="flex gap-16">
        <div className={classes.tableContainer}>
          <Table
            bgColor="white"
            cols={[
              { data: "Patient", accessor: "patient" },
              { data: "Invoice#", accessor: "invoiceNumber" },
              { data: "Invoice Date", accessor: "invoiceDate" },
              { data: "Refunded Amount", accessor: "refundedAmount" },
              { data: "Refunded Status", accessor: "refundedStatus" },
              { data: "Refund Mode", accessor: "refundedMode" },
              { data: "Refunded On", accessor: "refundedOn" },
            ]}
            data={tableData}
          />{" "}
          {hasNextPage && (
            <LoadMoreObserver
              noPaddingBottom
              loadMore={fetchNextPage}
              isLoading={isFetchingNextPage}
            />
          )}
          {isLoading && (
            <Box>
              <Skeleton count={8} height={40.5} borderRadius="0px" />
            </Box>
          )}
          {isTableDataEmpty && (
            <Box>
              <Empty position="center" />
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};

export default Refunds;
